import React, { useRef, useState } from 'react';
import { GuidePublicError, postGuidePin } from '../../../../../api/stay-guide-public-client';
import { Shake, StatusLabel } from '../../../../../motion';
import { Field, FieldError, Input, Label } from '../../../../onboarding/onboarding.styled';
import { Primary } from '../../../../onboarding/stay/public-stay-page.styled';
import { errorWords, t } from '../../../../onboarding/stay/stay-strings';
import { TITLE_ID } from './guide-frame';
import { Muted, Stack, Title } from './guide.styled';

const L = 'en' as const;
export const PIN_LENGTH = 5;

/** Only digits, at most five. */
export const pinDigits = (raw: string): string => raw.replace(/\D+/g, '').slice(0, PIN_LENGTH);

export const wrongPinWords = (left: number): string =>
  left === 1 ? t(L, 'guide.pinWrongOne') : t(L, 'guide.pinWrong', { n: Math.max(0, left) });

/**
 * THE GUIDE PIN (plan S7.7, SD-6) — asked only when the host turned it on.
 *
 * One numeric field (the phone's number pad and its one-time-code suggestion;
 * anything that is not a digit is dropped as it is typed), "Open my guide"
 * disabled until all five digits are in — with the reason on screen — and the
 * refusal under the field (role=alert) with a shake.
 *
 * The PIN is never stored, logged or put in the URL. A right PIN sets an
 * httpOnly cookie on the server's answer; the page then simply asks again.
 */
export const GuidePinGate: React.FC<{
  token: string;
  attemptsLeft: number;
  /** The PIN was right: ask for the guide again. */
  onUnlocked: () => void;
  /** 423: the server locked the guide; ask again to show the lock. */
  onLocked: () => void;
  /** 404 / 429: the page's own screens take over. */
  onRefused: (status: number) => void;
}> = ({ token, attemptsLeft, onUnlocked, onLocked, onRefused }) => {
  const [pin, setPin] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const field = useRef<HTMLInputElement | null>(null);
  const complete = pin.length === PIN_LENGTH;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!complete || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setProblem(null);
    try {
      await postGuidePin(token, pin);
      setPin('');
      onUnlocked();
    } catch (err) {
      const status = err instanceof GuidePublicError ? err.status : 0;
      if (status === 401) {
        const left = err instanceof GuidePublicError && err.attemptsLeft !== null ? err.attemptsLeft : attemptsLeft - 1;
        setProblem(wrongPinWords(left));
        setAttempt(a => a + 1);
        setPin('');
        if (field.current) field.current.focus();
      } else if (status === 400) {
        setProblem(t(L, 'guide.pinFormat'));
        setAttempt(a => a + 1);
      } else if (status === 423) {
        setPin('');
        onLocked();
      } else if (status === 404 || status === 429) {
        onRefused(status);
      } else {
        setProblem(errorWords('STAY_GUEST_ERROR'));
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate data-guide-pin="">
      <Stack>
        <Title id={TITLE_ID} tabIndex={-1}>
          {t(L, 'guide.pinTitle')}
        </Title>
        <Muted>{t(L, 'guide.pinHelper')}</Muted>
        <Field>
          <Label htmlFor="guide-pin">{t(L, 'guide.pinLabel')}</Label>
          <Shake active={attempt > 0} attempt={attempt}>
            <Input
              ref={field}
              id="guide-pin"
              name="guide-pin"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              autoComplete="one-time-code"
              maxLength={PIN_LENGTH}
              spellCheck={false}
              value={pin}
              onChange={ev => {
                setPin(pinDigits(ev.target.value));
                if (problem) setProblem(null);
              }}
              hasError={!!problem}
              aria-invalid={problem ? true : undefined}
              aria-describedby={problem ? 'guide-pin-error' : undefined}
              disabled={busy}
            />
          </Shake>
          <FieldError id="guide-pin-error" message={problem || undefined} />
        </Field>
        {!complete ? <Muted id="guide-pin-reason">{t(L, 'guide.pinIncomplete')}</Muted> : null}
        <Primary
          type="submit"
          disabled={!complete || busy}
          aria-describedby={!complete ? 'guide-pin-reason' : undefined}
          aria-busy={busy || undefined}
        >
          <StatusLabel
            state={busy ? 'busy' : 'idle'}
            labels={{ idle: t(L, 'guide.pinSubmit'), busy: t(L, 'guide.pinChecking') }}
          />
        </Primary>
      </Stack>
    </form>
  );
};
